// src/pages/seller/OrderManagementPage.jsx
import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useApp } from '../../context/AppContext';

const OrderManagementPage = () => {
  const { user } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const q = query(collection(db, 'orders'), where('sellerId', '==', user.uid));
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setOrders(ordersData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error("Order fetch error:", error);
        setError("Failed to load orders. Please check your internet connection.");
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [user?.uid]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { status: newStatus });
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending_payment': { text: '결제 대기', color: 'bg-yellow-100 text-yellow-800' },
      'confirmed': { text: '결제 완료', color: 'bg-green-100 text-green-800' },
      'processing': { text: '상품 준비 중', color: 'bg-blue-100 text-blue-800' },
      'shipped': { text: '배송 지시', color: 'bg-purple-100 text-purple-800' },
      'delivered': { text: '배송 완료', color: 'bg-gray-100 text-gray-800' },
      'cancelled': { text: '취소됨', color: 'bg-red-100 text-red-800' }
    };
    return statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
  };

  const renderActionButtons = (order) => {
    if (order.status === 'confirmed') {
      return (
        <button
          onClick={() => updateOrderStatus(order.id, 'processing')}
          className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
        >
          발주 확인 처리
        </button>
      );
    }
    if (order.status === 'processing') {
      return (
        <button
          onClick={() => updateOrderStatus(order.id, 'shipped')}
          className="bg-purple-600 text-white px-3 py-1 rounded text-xs hover:bg-purple-700"
        >
          운송장 등록
        </button>
      );
    }
    if (order.status === 'shipped') {
      return (
        <span className="text-gray-500 text-xs">택배사 처리 대기 중</span>
      );
    }
    return null;
  };

  if (user?.status === 'pending') {
    return (
      <div className="text-center py-12">
        <div className="bg-yellow-50 p-6 rounded-lg inline-block max-w-2xl">
          <h3 className="text-xl font-medium text-yellow-800 mb-2">Account Pending Approval</h3>
          <p className="text-yellow-700">
            Order management will be available after your account is approved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-medium text-gray-900 mb-6">Order Management</h1>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-2 text-gray-600">Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-50 p-6 rounded-lg inline-block max-w-2xl">
            <h3 className="text-xl font-medium text-gray-800 mb-2">No Orders Yet</h3>
            <p className="text-gray-600">
              You haven't received any orders yet. Once you get orders, they'll appear here.
            </p>
            <div className="mt-4 text-left bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Order Management Guide:</h4>
              <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                <li><strong>주문 확인</strong>: '결제 완료' 상태 주문 확인</li>
                <li><strong>발주 확인</strong>: '발주 확인 처리'로 상태 변경</li>
                <li><strong>배송 처리</strong>: 운송장 번호 등록 후 '배송 지시' 상태로 변경</li>
                <li><strong>자동 추적</strong>: 택배사 스캔 시 자동으로 상태 업데이트</li>
              </ol>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const statusInfo = getStatusBadge(order.status);
            return (
              <div key={order.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-medium">Order #{order.id}</h3>
                    <p className="text-gray-600 text-sm">
                      {order.createdAt?.seconds 
                        ? new Date(order.createdAt.seconds * 1000).toLocaleDateString('ko-KR')
                        : 'Unknown date'
                      }
                    </p>
                    <p className="text-gray-900 font-medium mt-1">${order.totalAmount}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs ${statusInfo.color}`}>
                      {statusInfo.text}
                    </span>
                    <div className="mt-2">
                      {renderActionButtons(order)}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <p>Buyer: {order.buyerEmail || 'N/A'}</p>
                  <p>Items: {order.items?.length || 0} products</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderManagementPage;